/**
 * VectorFlow — Demo App
 * Wires up file inputs, VectorFlow instance, and UI buttons.
 */

import VectorFlow from '@nitesh-tyagi/vectorflow';

// ---- DOM References ----
const svgFileInput = document.getElementById('svg-file');
const jsonFileInput = document.getElementById('json-file');
const svgFileName = document.getElementById('svg-file-name');
const jsonFileName = document.getElementById('json-file-name');
const loadBtn = document.getElementById('load-btn');
const svgViewport = document.getElementById('svg-viewport');
const placeholder = document.getElementById('placeholder');
const currentStateEl = document.getElementById('current-state');
const currentActionEl = document.getElementById('current-action');
const actionButtonsEl = document.getElementById('action-buttons');
const errorDisplay = document.getElementById('error-display');
const errorMessage = document.getElementById('error-message');
const errorClose = document.getElementById('error-close');

// ---- State ----
let svgText = null;
let jsonText = null;
let vf = null; // VectorFlow instance

// ---- File Input Handlers ----

svgFileInput.addEventListener('change', (e) => {
  const file = e.target.files[0];
  if (file) {
    svgFileName.textContent = file.name;
    const reader = new FileReader();
    reader.onload = (ev) => {
      svgText = ev.target.result;
      updateLoadButton();
    };
    reader.readAsText(file);
  }
});

jsonFileInput.addEventListener('change', (e) => {
  const file = e.target.files[0];
  if (file) {
    jsonFileName.textContent = file.name;
    const reader = new FileReader();
    reader.onload = (ev) => {
      jsonText = ev.target.result;
      updateLoadButton();
    };
    reader.readAsText(file);
  }
});

function updateLoadButton() {
  loadBtn.disabled = !(svgText && jsonText);
}

// ---- Load Button ----

loadBtn.addEventListener('click', () => {
  hideError();

  // Destroy previous instance
  if (vf) {
    vf.destroy();
    vf = null;
  }

  try {
    // Parse SVG into DOM
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgElement = svgDoc.querySelector('svg');

    if (!svgElement) {
      throw new Error('No <svg> element found in the uploaded file.');
    }

    // Check for parse errors
    const parseError = svgDoc.querySelector('parsererror');
    if (parseError) {
      throw new Error('SVG parsing error: ' + parseError.textContent.slice(0, 200));
    }

    // Insert SVG into viewport
    svgViewport.innerHTML = '';
    svgViewport.appendChild(document.importNode(svgElement, true));
    const liveSvg = svgViewport.querySelector('svg');

    // Create VectorFlow instance
    vf = new VectorFlow({
      svgElement: liveSvg,
      json: jsonText,
    });

    // Update status
    currentStateEl.textContent = vf.currentState;
    currentActionEl.textContent = '—';

    // Wire events
    vf.on('stateChange', (state) => {
      currentStateEl.textContent = state;
    });

    vf.on('actionStart', (name) => {
      currentActionEl.textContent = name;
      updateActionButtonStates(name);
    });

    vf.on('actionEnd', () => {
      currentActionEl.textContent = '—';
      updateActionButtonStates(null);
    });

    vf.on('error', (err) => {
      showError(err.message);
    });

    // Build action buttons
    buildActionButtons(vf.actions);

  } catch (err) {
    showError(err.message);
    console.error('VectorFlow load error:', err);
  }
});

// ---- Action Buttons ----

function buildActionButtons(actions) {
  actionButtonsEl.innerHTML = '';

  for (const actionName of Object.keys(actions)) {
    const btn = document.createElement('button');
    btn.className = 'action-btn';
    btn.textContent = actionName;
    btn.dataset.action = actionName;
    btn.addEventListener('click', () => {
      if (!vf) return;
      hideError();
      vf.play(actionName).catch((err) => {
        if (err.name !== 'AbortError') {
          showError(err.message);
        }
      });
    });
    actionButtonsEl.appendChild(btn);
  }

  // Stop button
  const stopBtn = document.createElement('button');
  stopBtn.className = 'stop-btn';
  stopBtn.textContent = 'Stop';
  stopBtn.addEventListener('click', () => {
    if (vf) vf.stop();
  });
  actionButtonsEl.appendChild(stopBtn);
}

function updateActionButtonStates(activeAction) {
  const buttons = actionButtonsEl.querySelectorAll('.action-btn');
  for (const btn of buttons) {
    btn.classList.toggle('active', btn.dataset.action === activeAction);
  }
}

// ---- Error Display ----

function showError(msg) {
  errorMessage.textContent = msg;
  errorDisplay.style.display = 'flex';
}

function hideError() {
  errorDisplay.style.display = 'none';
  errorMessage.textContent = '';
}

errorClose.addEventListener('click', hideError);
